import { Schema } from "effect"
import { Actor, EntityRef } from "../common"
import { AgentId, CommentId, Handle, Millis, NonNegInt, RunId, Seq, ShortRef, UserId } from "../ids"

// #region messaging
/**
 * Decision 0142 (messaging phase 1): a person's own external participant: their ChatGPT dot (`agent`) or one of their
 * coding sessions connected to Minga's MCP (`session`). Minted by the Workspace DO's registry (`ptc_` + 32 hex).
 */
export const ParticipantId = Schema.String.check(Schema.isPattern(/^ptc_[0-9a-f]{32}$/)).pipe(
  Schema.brand("ParticipantId"),
)
export type ParticipantId = typeof ParticipantId.Type

export const ParticipantKind = Schema.Literals(["agent", "session"])
export type ParticipantKind = typeof ParticipantKind.Type

/**
 * Who a message is from or to (decision 0142). Agents, people and live worker runs are the project's own; `external` is
 * a person's participant as it was named when the message was sent (`handle` is the person's own short name, `dot`,
 * `claude-code`, shown as `@jannik.dot`). An external participant speaks only for its owner and sees what they see.
 */
export const MessageParty = Schema.TaggedUnion({
  agent: { agentId: AgentId },
  user: { userId: UserId },
  /** A worker or validator run (W-n). Messages to it steer it while it is live. */
  worker: { runId: RunId, agentId: AgentId },
  external: {
    participantId: ParticipantId,
    ownerId: UserId,
    handle: Handle,
    kind: ParticipantKind,
    /** The MCP client's short name (`chatgpt`, `claude-code`, `codex`). */
    client: Schema.String,
    /** The OAuth client the participant is reached through (a session's next tool call picks its messages up). */
    clientId: Schema.optionalKey(Schema.String),
  },
})
export type MessageParty = typeof MessageParty.Type

/** How a message reaches one recipient (decision 0142). Every delivery is recorded (`MessageDelivered`). */
export const MessageRoute = Schema.Literals([
  /** An agent: a wake through the Project's outbox (its next turn, or now when idle). */
  "wake",
  /** A live worker or validator run: a steer after its current tool call. */
  "steer",
  /** A worker run that is not live: its next attempt reads the thread. */
  "next_attempt",
  /** A person: a bell notice, and `message.received` on their MCP Events subscriptions. */
  "notice",
  /** A person's external agent: `message.received` (webhook or poll) and `read_messages`. */
  "event",
  /** A person's coding session: with its next Minga tool call, `read_messages` or `events/poll`. */
  "session",
])
export type MessageRoute = typeof MessageRoute.Type

/** `queued`: on its way (in the outbox or waiting for the participant); `read`: the participant took it. */
export const MessageDeliveryState = Schema.Literals(["queued", "read"])
export type MessageDeliveryState = typeof MessageDeliveryState.Type

/** A message to an external participant that it has not picked up yet. */
export const PendingMessage = Schema.Struct({
  commentId: CommentId,
  participantId: ParticipantId,
  ownerId: UserId,
  kind: ParticipantKind,
  clientId: Schema.optionalKey(Schema.String),
  at: Millis,
  /** The agent that sent it (a worker's: its owner agent): it hears when the message is not picked up. */
  sender: Schema.optionalKey(AgentId),
})
export type PendingMessage = typeof PendingMessage.Type

/**
 * Decision 0142: a conversation (`T-n`). Every message is a comment whose `threadId` is the thread's id (the first
 * message's comment id). A thread is attached to a record (`on`: an item, a card, a goal) or to nothing (a direct thread
 * between its parties, whose comments target the thread itself). Cards and chat stay separate; a thread can link them.
 * Thread refs are numbered after the old tasks' (`refCounters.threads`, starting past `refCounters.T`), so they never
 * meet an old `T-n` item ref.
 */
export const Thread = Schema.Struct({
  id: CommentId,
  ref: ShortRef,
  on: Schema.NullOr(EntityRef),
  /** Everyone who wrote in it or was written to, in order of first appearance. */
  parties: Schema.Array(MessageParty),
  createdBy: Actor,
  createdAt: Millis,
  lastAt: Millis,
  lastSeq: Seq,
  messages: NonNegInt,
  /** Messages to external participants not picked up yet (shown as queued; a day later the sender hears of it). */
  pending: Schema.Array(PendingMessage),
})
export type Thread = typeof Thread.Type
// #endregion messaging
